/**
 * Form Builder Service
 * Creates and manages FormGroup instances from field configurations
 */

import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { GenericFormUICDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';
import { IFormField, FormField } from '../models';
import { ValidationService } from './validation.service';

/**
 * Service for building FormGroups from UIC configurations
 */
@Injectable({ providedIn: 'root' })
export class FormBuilderService {
  constructor(private validationService: ValidationService) {}
  
  /**
   * Build a FormGroup from field configurations
   * @param fieldConfigs Array of field configuration DTOs
   * @returns Object containing the FormGroup and enhanced FormField array
   */
  buildFormGroup(fieldConfigs: GenericFormUICDTO[]): { 
    formGroup: FormGroup; 
    formFields: IFormField[];
  } {
    const group: { [key: string]: FormControl } = {};
    const formFields: IFormField[] = [];
    
    fieldConfigs.forEach(fieldDTO => {
      // Convert DTO to enhanced FormField
      const field = FormField.fromDTO(fieldDTO);
      
      // Skip if no field name
      if (!field.dbFeildName) {
        console.warn('[FormBuilderService] Skipping field without dbFeildName:', field);
        return;
      }
      
      // Build validators
      const validators = this.buildValidators(field);
      
      // Create FormControl
      const control = new FormControl(
        field.defaultValue || null,
        validators
      );
      
      // Store control reference in field
      field.control = control;
      
      // Add to group
      group[field.dbFeildName] = control;
      formFields.push(field);
    });
    
    const formGroup = new FormGroup(group);
    
    console.log(`[FormBuilderService] Built form with ${formFields.length} fields`);
    
    return { formGroup, formFields };
  }
  
  /**
   * Build validators for a field
   * @param field The field configuration
   * @returns Array of validators
   */
  private buildValidators(field: IFormField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    
    // Required validation
    if (field.isRequired) {
      validators.push(Validators.required);
    }
    
    // Pattern validation
    if (field.validationPatternDetailL) {
      const pattern = this.validationService.getPattern(field.validationPatternDetailL);
      if (pattern) {
        validators.push(Validators.pattern(pattern));
      }
    }
    
    // Field type specific validations
    switch (field.uiInputType?.toLowerCase()) {
      case 'email':
        validators.push(Validators.email);
        break;
      
      case 'number':
        // Add number validators if needed
        break;
      
      case 'url':
        const urlPattern = this.validationService.getPattern('url');
        if (urlPattern) {
          validators.push(Validators.pattern(urlPattern));
        }
        break;
    }
    
    return validators;
  }
  
  /**
   * Patch form with data
   * @param formGroup The form group to patch
   * @param data The data object
   * @param fields The field configurations
   */
  patchFormData(formGroup: FormGroup, data: any, fields: IFormField[]): void {
    if (!data) {
      console.warn('[FormBuilderService] No data to patch');
      return;
    }
    
    fields.forEach(field => {
      if (!field.dbFeildName) return;
      
      const value = data[field.dbFeildName];
      
      if (value !== undefined && value !== null) {
        const control = formGroup.get(field.dbFeildName);
        if (control) {
          control.setValue(value, { emitEvent: false });
        }
      }
    });
    
    console.log('[FormBuilderService] Patched form data');
  }
  
  /**
   * Extract form data
   * @param formGroup The form group
   * @param fields The field configurations
   * @returns Object containing form values
   */
  extractFormData(formGroup: FormGroup, fields: IFormField[]): any {
    const data: any = {};
    
    fields.forEach(field => {
      if (!field.dbFeildName) return;
      
      const value = formGroup.get(field.dbFeildName)?.value;
      data[field.dbFeildName] = value;
    });
    
    return data;
  }
  
  /**
   * Reset form to default values
   * @param formGroup The form group to reset
   * @param fields The field configurations
   */
  resetForm(formGroup: FormGroup, fields: IFormField[]): void {
    fields.forEach(field => {
      if (!field.dbFeildName) return;
      
      const control = formGroup.get(field.dbFeildName);
      if (control) {
        control.reset(field.defaultValue || null);
      }
    });
    
    console.log('[FormBuilderService] Reset form');
  }
  
  /**
   * Validate entire form and collect errors
   * @param formGroup The form group to validate
   * @param fields The field configurations
   * @returns Array of error messages
   */
  validateForm(formGroup: FormGroup, fields: IFormField[]): string[] {
    const errors: string[] = [];
    
    fields.forEach(field => {
      if (!field.dbFeildName) return;
      
      const control = formGroup.get(field.dbFeildName);
      if (control && control.invalid && control.touched) {
        const fieldErrors = this.validationService.getErrorMessages(control, field.uiLabel || 'Field');
        errors.push(...fieldErrors);
      }
    });
    
    return errors;
  }
  
  /**
   * Mark all fields as touched (to show validation errors)
   * @param formGroup The form group
   */
  markAllAsTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key)?.markAsTouched();
    });
  }
}
